import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { CLINIC } from '../data/clinic';
import { useHideOnScroll } from '../hooks/useHideOnScroll';

// Floating WhatsApp chat button — lets patients message the doctor with questions from any page.
// The pre-filled message is tailored to the page the patient is on.
const MESSAGES = [
  { match: '/decompression', text: 'Hi, I have a question about spinal decompression (DRX9000).' },
  { match: '/walk-in', text: "Hi, I'd like to ask about walk-in / same-day care." },
  { match: '/personal-injury', text: 'Hi, I have a question about care after a car or work accident.' },
  { match: '/technology', text: 'Hi, I have a question about your treatment technology.' },
  { match: '/services', text: 'Hi, I have a question about your services.' },
  { match: '/conditions', text: 'Hi, I have a question about a condition and whether you can help.' },
  { match: '/contact', text: 'Hi, I have a question for Dr. Ruiz.' },
];

const messageForPath = (path) => {
  const hit = MESSAGES.find((m) => path.startsWith(m.match));
  return hit ? hit.text : 'Hi, I have a question for Dr. Ruiz.';
};

const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.372-.025-.521-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.548 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const WhatsAppButton = () => {
  const { pathname } = useLocation();
  const hidden = useHideOnScroll();
  const href = `https://wa.me/${CLINIC.whatsapp}?text=${encodeURIComponent(messageForPath(pathname))}`;
  return (
    <motion.a
      href={href}
      target="_blank"
      rel="noreferrer"
      data-testid="whatsapp-float-btn"
      whileHover={{ y: -3 }}
      animate={{ y: hidden ? 140 : 0, opacity: hidden ? 0 : 1 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      className="fixed bottom-24 left-4 z-40 inline-flex items-center gap-3 rounded-full bg-[#25D366] px-5 py-4 text-[15px] font-semibold text-white transition-colors hover:bg-[#1EBE5B] xl:bottom-8 xl:left-8"
      style={{ boxShadow: '0 16px 40px rgba(37,211,102,0.35)', pointerEvents: hidden ? 'none' : 'auto' }}
      aria-label="Message us on WhatsApp"
    >
      <WhatsAppIcon className="h-6 w-6" />
      <span className="hidden sm:inline">Questions? WhatsApp Us</span>
    </motion.a>
  );
};

export default WhatsAppButton;
